import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, ListChecks, Layers, Wrench, GitCompare, BookOpen, Calculator, Newspaper, GraduationCap, Sparkles, Database, LineChart } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcCategories, bcTools } from "@/lib/api";
import { TalkToAiEmployeeButton } from "@/components/TalkToAiEmployeeLauncher";

const JOURNEY = [
  { key: "discover", label: "Discover",  desc: "See what AI can actually do for your business — plainly." },
  { key: "learn",    label: "Learn",     desc: "Guides, playbooks and the AI Academy." },
  { key: "compare",  label: "Compare",   desc: "Side-by-side product comparisons scored on 100 points." },
  { key: "assess",   label: "Assess",    desc: "20-category AI Readiness Assessment for your business." },
  { key: "plan",     label: "Plan",      desc: "Stack Builder + 30/60/90-day roadmap." },
  { key: "implement",label: "Implement", desc: "Approved implementation via WebsiteTech.pro." },
  { key: "measure",  label: "Measure",   desc: "Attribution + client AI portfolio + monthly reports." },
  { key: "improve",  label: "Improve",   desc: "Continuous feedback loops + Growth Agent." },
];

const SECTIONS = [
  { key: "assessment",     icon: ListChecks,   to: "/business-center/assessment",   label: "AI Readiness Assessment", desc: "20-category scored assessment for SMBs — free, no signup." },
  { key: "stack",          icon: Layers,       to: "/business-center/stack-builder", label: "AI Stack Builder",       desc: "Recommendation engine that assembles your right AI toolkit." },
  { key: "tools",          icon: Database,     to: "/business-center/tools",         label: "Tool Directory",         desc: "Business AI tools scored on our transparent 100-point scale." },
  { key: "compare",        icon: GitCompare,   to: "/business-center/compare",       label: "Compare Tools",          desc: "2–4 products side-by-side with a personalized Business Fit Score." },
  { key: "playbooks",      icon: BookOpen,     to: "/business-center/playbooks",     label: "Industry Playbooks",     desc: "Step-by-step playbooks for 18+ verticals." },
  { key: "guides",         icon: Wrench,       to: "/business-center/guides",        label: "How-To Guides",          desc: "Practical guides organized by difficulty, industry and objective." },
  { key: "calculators",    icon: Calculator,   to: "/business-center/calculators",   label: "ROI Calculators",        desc: "12 transparent calculators — every formula visible and editable." },
  { key: "case-studies",   icon: Sparkles,     to: "/business-center/case-studies",  label: "Case Studies",           desc: "Verified customer results, clearly labeled — no fiction." },
  { key: "news",           icon: Newspaper,    to: "/business-center/news",          label: "AI News",                desc: "Business AI news with what to do about it." },
  { key: "academy",        icon: GraduationCap,to: "/business-center/academy",       label: "AI Academy",             desc: "Quick-start training + prompt library for your team." },
  { key: "brief",          icon: Compass,      to: "/business-center/brief",         label: "Weekly AI Business Brief",desc: "One development, one product, one automation, one action — every week." },
  { key: "measure",        icon: LineChart,    to: "/business-center/measure",       label: "Measure & Improve",       desc: "Client AI Portfolio Manager (Phase 2)." },
];

export default function BusinessCenter() {
  const [stats, setStats] = useState({ tools: 0, categories: 0 });
  useEffect(() => {
    document.title = "AI Business Center | WebsiteTech.pro";
    (async () => {
      try {
        const [c, t] = await Promise.all([bcCategories(), bcTools()]);
        setStats({ tools: (t.items || []).length, categories: (c.categories || []).length });
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1" data-testid="business-center">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-16 text-white bg-slate-950">
          <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-indigo-600 opacity-30 blur-3xl pointer-events-none" />
          <div className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full bg-emerald-500 opacity-20 blur-3xl pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> WebsiteTech AI Business Center
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-tight">
              Understand AI. Choose the right tools. Implement. <span className="text-indigo-400">Measure the results.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-3xl">
              Independent education, transparent scoring, and a direct path from insight to implementation — for small and medium businesses ready to grow with AI.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/business-center/assessment"
                data-testid="bc-hero-assess"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-6 py-3.5 font-semibold transition"
              >
                Start the free assessment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/business-center/methodology"
                data-testid="bc-hero-methodology"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3.5 font-semibold transition"
              >
                Read our scoring methodology
              </Link>
              <TalkToAiEmployeeButton size="md" variant="ghost" />
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-slate-400">
              <span data-testid="bc-stat-tools">{stats.tools} tools scored</span>
              <span>·</span>
              <span data-testid="bc-stat-categories">{stats.categories} categories tracked</span>
              <span>·</span>
              <span>Every score sourced, every price date-stamped, every affiliate relationship disclosed</span>
            </div>
          </div>
        </section>

        {/* Journey strip */}
        <section className="py-14 bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">The journey</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">Discover → Learn → Compare → Assess → Plan → Implement → Measure → Improve</h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {JOURNEY.map((j, i) => (
                <div key={j.key} className="relative rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`bc-journey-${j.key}`}>
                  <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-indigo-600 text-white grid place-items-center font-bold text-[11px]">{i + 1}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{j.label}</div>
                  <div className="mt-1 text-[11px] text-slate-600 leading-relaxed">{j.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section grid */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Everything you need to make an AI decision</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">Every resource here connects to a concrete action — assess your business, build your stack, request implementation, and measure the outcome.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.key} to={s.to} data-testid={`bc-section-${s.key}`}
                        className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-indigo-400 hover:shadow-md transition">
                    <div className="w-11 h-11 rounded-lg bg-indigo-50 grid place-items-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="mt-4 font-semibold text-slate-900">{s.label}</div>
                    <div className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:gap-2 transition-all">
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="py-16 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8">
            {[
              ["Independent", "Recommendations follow rules, not affiliate commissions. Commercial relationships are disclosed on every review."],
              ["Transparent", "100-point scoring, published methodology, evidence + testing dates on every score."],
              ["Actionable",  "Every insight leads to a concrete next step — assess, plan, request implementation."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid={`bc-trust-${t.toLowerCase()}`}>
                <div className="text-lg font-bold">{t}</div>
                <div className="mt-2 text-sm text-slate-300 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
